import React, { useEffect, useCallback, useState } from "react";
import { motion } from "framer-motion";
import Filters from "./Filters";
import Toolbar from "./Toolbar";
import RatingTable from "./RatingTable";
import SingleUserPanel from "./SingleUserPanel";
import { useStaffRatingApi } from "./useStaffRatingApi";
import "./EmployeeRating.css";

export default function StaffPerformanceDashboard({
  apiBase = "https://lttsbackend.axiomos.in",
  defaultInclude = "activity",
  className = "",
}) {
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedProject, setSelectedProject] = useState("");
  const [period, setPeriod] = useState("month");
  const [include, setInclude] = useState(defaultInclude);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const {
    users,
    projects,
    data,
    loading,
    error,
    refetch,
    listResults,
    single,
    setApiBase,
  } = useStaffRatingApi({
    apiBase,
    selectedUser,
    selectedProject,
    period,
    include,
    from,
    to,
  });

  /* ---------- Sync prop changes ---------- */
  useEffect(() => setApiBase(apiBase), [apiBase, setApiBase]);

  /* ---------- Reset custom date range when period changes ---------- */
  useEffect(() => {
    if (period !== "custom") {
      setFrom("");
      setTo("");
    }
  }, [period]);

  const onRowClick = useCallback((row) => {
    if (!row?.userId && !row?._id) return;
    setSelectedUser(row.userId || row._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onBackToList = useCallback(() => setSelectedUser("all"), []);

  /* ---------- CSV Export ---------- */
  const handleExport = useCallback(() => {
    if (!listResults?.length) return;
    const headers = Object.keys(listResults[0]);
    const csv = [
      headers.join(","),
      ...listResults.map((r) =>
        headers.map((h) => JSON.stringify(r[h] ?? "")).join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staff_performance_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [listResults]);

  return (
    <div className={`spd ${className}`}>
      {/* ---------- Header ---------- */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="spd__header"
      >
        <h1 className="spd__title">Staff Performance Dashboard</h1>
        <p className="spd__subtitle">
          Explore ratings, activity, and trends across trainings, UaUC, and work
          permits.
        </p>
      </motion.div>

      {/* ---------- Filters ---------- */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Filters
          users={users}
          projects={projects}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          period={period}
          setPeriod={setPeriod}
          include={include}
          setInclude={setInclude}
          from={from}
          setFrom={setFrom}
          to={to}
          setTo={setTo}
        />
      </motion.div>

      {/* ---------- Toolbar ---------- */}
      <Toolbar
        loading={loading}
        error={error}
        isSingle={selectedUser !== "all"}
        listResults={listResults}
        onRefresh={refetch}
        onExport={handleExport}
      />

      {/* ---------- Main Body ---------- */}
      {selectedUser === "all" ? (
        <section className="spd__section">
          <h2 className="spd__sectionTitle">Top Performers</h2>
          <RatingTable
            rows={listResults}
            include={include}
            onRowClick={onRowClick}
          />
        </section>
      ) : (
        <SingleUserPanel data={single} include="full" onBack={onBackToList} />
      )}
    </div>
  );
}
